/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CmReasonResponse } from '../models/cm-reason-response';
import { CreateCmReasonRequest } from '../models/create-cm-reason-request';
import { GenericResponse } from '../models/generic-response';
import { GetCmReasonResponse } from '../models/get-cm-reason-response';
import { UpdateCmReasonRequest } from '../models/update-cm-reason-request';

/**
 * Credit Note Reason APIs
 */
@Injectable({ providedIn: 'root' })
export class CmReasonService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllCmReasons()` */
  static readonly GetAllCmReasonsPath = '/api/v1/cm-reasons';

  /**
   * Get list of Credit Note Reasons.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllCmReasons()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllCmReasons$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       *   - reason
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetCmReasonResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CmReasonService.GetAllCmReasonsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetCmReasonResponse>;
        })
      );
  }

  /**
   * Get list of Credit Note Reasons.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllCmReasons$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllCmReasons(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       *   - reason
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetCmReasonResponse> {
    return this.getAllCmReasons$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetCmReasonResponse>): GetCmReasonResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createCmReason()` */
  static readonly CreateCmReasonPath = '/api/v1/cm-reasons';

  /**
   * create Credit Note Reasons.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createCmReason()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCmReason$Response(
    params: {
      body: CreateCmReasonRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CmReasonResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CmReasonService.CreateCmReasonPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CmReasonResponse>;
        })
      );
  }

  /**
   * create Credit Note Reasons.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createCmReason$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCmReason(
    params: {
      body: CreateCmReasonRequest;
    },
    context?: HttpContext
  ): Observable<CmReasonResponse> {
    return this.createCmReason$Response(params, context).pipe(
      map((r: StrictHttpResponse<CmReasonResponse>): CmReasonResponse => r.body)
    );
  }

  /** Path part for operation `getCmReasonById()` */
  static readonly GetCmReasonByIdPath = '/api/v1/cm-reasons/{id}';

  /**
   * fetch Credit Note Reasons details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCmReasonById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCmReasonById$Response(
    params: {
      /**
       * The cm_reason id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CmReasonResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CmReasonService.GetCmReasonByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CmReasonResponse>;
        })
      );
  }

  /**
   * fetch Credit Note Reasons details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCmReasonById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCmReasonById(
    params: {
      /**
       * The cm_reason id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<CmReasonResponse> {
    return this.getCmReasonById$Response(params, context).pipe(
      map((r: StrictHttpResponse<CmReasonResponse>): CmReasonResponse => r.body)
    );
  }

  /** Path part for operation `deleteCmReasonById()` */
  static readonly DeleteCmReasonByIdPath = '/api/v1/cm-reasons/{id}';

  /**
   * Delete Credit Note Reasons  by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCmReasonById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCmReasonById$Response(
    params: {
      /**
       * The cm reason id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CmReasonService.DeleteCmReasonByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete Credit Note Reasons  by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteCmReasonById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCmReasonById(
    params: {
      /**
       * The cm reason id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteCmReasonById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateCmReason()` */
  static readonly UpdateCmReasonPath = '/api/v1/cm-reasons/{id}';

  /**
   * update credit note reasons.
   *
   * update Cm Reason
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCmReason()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCmReason$Response(
    params: {
      /**
       * The cm reason id that needs to be updated
       */
      id: number;
      body: UpdateCmReasonRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CmReasonResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CmReasonService.UpdateCmReasonPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CmReasonResponse>;
        })
      );
  }

  /**
   * update credit note reasons.
   *
   * update Cm Reason
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCmReason$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCmReason(
    params: {
      /**
       * The cm reason id that needs to be updated
       */
      id: number;
      body: UpdateCmReasonRequest;
    },
    context?: HttpContext
  ): Observable<CmReasonResponse> {
    return this.updateCmReason$Response(params, context).pipe(
      map((r: StrictHttpResponse<CmReasonResponse>): CmReasonResponse => r.body)
    );
  }
}
